/**
 * Apple IAP Service — calls backend endpoints for Apple IAP verification.
 */

import { PurchaseDetails } from '../types/purchase';

import axiosInstance from './axiosInstance';

export interface VerifyTransactionRequest {
  jws_transaction: string;
  product_type: 'credits' | 'subscription';
}

export interface VerifyTransactionResponse {
  status: string;
  payment_id?: string;
  credits_granted?: number;
  tier?: string;
  interval?: string;
  product_id?: string;
  purchase?: PurchaseDetails;
}

export interface AppleSubscriptionStatus {
  has_apple_subscription: boolean;
  subscription_tier?: string;
  subscription_status?: string;
  apple_original_transaction_id?: string;
  subscription_provider?: string;
}

export interface AppleProductInfo {
  product_id: string;
  type: 'credits' | 'subscription';
  dollar_value?: number;
  credits?: number;
  tier?: string;
  interval?: string;
  plan_name?: string;
  monthly_credits?: number;
}

export interface AppleProductsResponse {
  credit_products: AppleProductInfo[];
  subscription_products: AppleProductInfo[];
}

/**
 * Verify an Apple StoreKit 2 JWS-signed transaction with the backend.
 */
export async function verifyTransaction(
  jwsTransaction: string,
  productType: 'credits' | 'subscription',
): Promise<VerifyTransactionResponse> {
  const response = await axiosInstance.post<VerifyTransactionResponse>('/api/v1/apple-iap/verify-transaction', {
    jws_transaction: jwsTransaction,
    product_type: productType,
  });

  return response.data;
}

/**
 * Get Apple subscription status for the current user.
 */
export async function getAppleSubscriptionStatus(): Promise<AppleSubscriptionStatus> {
  const response = await axiosInstance.get<AppleSubscriptionStatus>('/api/v1/apple-iap/subscription-status');

  return response.data;
}

/**
 * Get product mapping with tier-specific credit counts.
 */
export async function getAppleProducts(): Promise<AppleProductsResponse> {
  const response = await axiosInstance.get<AppleProductsResponse>('/api/v1/apple-iap/products');

  return response.data;
}

export interface VerifyAndFulfillResponse {
  status: string; // 'success', 'already_active', 'already_processed', 'error'
  error_code?: string; // 'APPLE_ACCOUNT_CONFLICT'
  message?: string;
  payment_id?: string;
  credits_granted?: number;
  tier?: string;
  interval?: string;
  product_id?: string;
  purchase?: PurchaseDetails;
}

/**
 * Primary fulfillment path for Apple IAP.
 * Handles both fresh purchases and restored transactions.
 * Returns specific error codes for Apple account conflicts.
 */
export async function verifyAndFulfill(
  jwsTransaction: string,
  productType: 'credits' | 'subscription',
  isRestore: boolean = false,
): Promise<VerifyAndFulfillResponse> {
  const response = await axiosInstance.post<VerifyAndFulfillResponse>('/api/v1/apple-iap/verify-and-fulfill', {
    jws_transaction: jwsTransaction,
    product_type: productType,
    is_restore: isRestore,
  });

  return response.data;
}
